import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Stack } from "@mui/material";

import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import { densityOptions } from "components/tables/columns/ColumnManager";

export type NarrativeTableSettingsStateType = {
  customWidgetTitle: string;
  density: string;
  rowStripes?: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: NarrativeTableSettingsStateType = {
  customWidgetTitle: '',
  density: "standard",
  rowStripes: false,
  widgetTitle: 'none',
  widgetTitleAlignment: 'left',
}

const defaultWidgetRequest = {
  name: 'narrative-table',
  endpoint: 'query/cluster-graph',
  queryParams: { },
  requestInit: {
    body: {
      clusters: { size: 100 }
    }
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultNarrativeTableRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    clusters: {
      size: 100
    }
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function NarrativeTableSettings(props: WidgetSettingsProps) : JSX.Element {
  const { i18n } = useTranslation();

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const [settingsState, setSettingsState] = useState<NarrativeTableSettingsStateType>({
      ...defaultSettings,
      ...props.widget?.data?.settings,
    });
  const settingsOnChange = (updatedState: NarrativeTableSettingsStateType) => setSettingsState(updatedState);

  const updateWidgetData = () => {
    props.saveWidgetData({
      settings: settingsState
    }).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  };

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      maxWidth="xs"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidgetData();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("display")]}
      title={i18n.t("widget-types.narrative-table.settings.title")}
    >
      <>
        {tabValue === 0 &&
          <SplitSettingsWrapper>
            <SettingsOptions
              onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")}>
                <SettingsToggleButton
                  keyid="density"
                  label={i18n.t("tables.row-density")}
                  optionsList={densityOptions}
                  value={settingsState.density} />
                <SettingsCheckbox
                  keyid="rowStripes"
                  label={i18n.t("tables.row-stripes")}
                  checked={settingsState.rowStripes}
                />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }

      </>
    </TabbedDialog>
  )
}